import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  BoosterCards,
  boosterData,
  getCardsForBooster,
  updateBooster,
} from "../../../services/BoosterServices";
import { useTheme } from "../../../context/ThemeProvider";
import styles from "./AddBoosterToStoreModal.module.css";
import globalStyles from "../../../styles/globalStyles.module.css";
import { FaCircleInfo } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../../context/AlertProvider";
import { getStripeAccountStatus } from "../../../services/StripeServices";
import { stripeAccountStatus } from "../../../enums/stripeAccountStatusEnum";
import { Link } from "react-router-dom";

// Define the props type for the modal
interface AddBoosterToStoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  booster: boosterData;
}

const AddBoosterToStoreModal: React.FC<AddBoosterToStoreModalProps> = ({
  isOpen,
  onClose,
  booster,
}) => {
  const [boosterPrice, setBoosterPrice] = useState<number>(booster.price ?? 0);
  const { currentTheme } = useTheme();
  const [cards, setCards] = useState<BoosterCards[]>([]);
  const [accountStatus, setAccountStatus] = useState<stripeAccountStatus>(
    stripeAccountStatus.not_created
  );
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  useEffect(() => {
    const fetchCards = async () => {
      if (!booster) return;

      try {
        const fetchedCards = await getCardsForBooster(booster.id);
        setCards(fetchedCards);
      } catch (error) {
        console.error("Error fetching cards:", error);
      }
    };

    if (isOpen && cards?.length === 0) {
      fetchCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booster, isOpen]);

  useEffect(() => {
    const fetch = async () => {
      const status = await getStripeAccountStatus();
      setAccountStatus(status);
    };

    fetch();
  }, []);

  if (!isOpen) return null;

  const handleBoosterPrice = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;

    inputValue = inputValue.replace(/[^0-9.,]/g, "");

    if (inputValue.includes(",")) {
      inputValue = inputValue.replace(",", ".");
    }

    var parsedValue = Number(parseFloat(inputValue).toFixed(2));

    if (parsedValue > 20) {
      parsedValue = 20;
    }
    setBoosterPrice(Number(parsedValue));
  };

  const handleAddToStore = async () => {
    booster.price = boosterPrice;
    booster.is_in_store = !booster.is_in_store;

    const {
      id,
      created_at,
      updated_at,
      image_id,
      original_image_id,
      quantity_sold,
      ...updatedBooster
    } = booster;

    try {
      await updateBooster(booster.id, updatedBooster);
      showAlert(t("boosters.addToStore.success"));
    } catch (error) {
      booster.is_in_store = !booster.is_in_store;
      console.log("Error updating booster:", error);
      if (error instanceof Error) {
        showAlert(error.message);
      } else {
        showAlert(t("errors.unexpectedError"));
      }
    }

    onClose();
  };

  const handleCloseModal = () => {
    onClose();
  };

  return (
    <Modal
      show={isOpen}
      onHide={handleCloseModal}
      size="xl"
      className={`${currentTheme} d-flex`}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <div className={styles.container}>
        <img
          src={booster.image_id}
          alt="Booster"
          className={styles.imageContainer}
        />
        <div className={styles.infoContainer}>
          {booster.is_in_store ? (
            <h2>Removing Booster from store</h2>
          ) : (
            <h2>Adding Booster to store</h2>
          )}
          <h3>{booster.name}</h3>
          <h4>{booster.description}</h4>
          <p>There is {cards?.length} cards in this booster</p>
          {cards?.length === 0 && (
            <p>
              Please add {cards?.length % 9} more card to complete this booster
            </p>
          )}
          {/* <h5>Rarity distribution</h5> */}
          {/* <p>Gold : {rarityDistribution.gold}</p>
          <p>Silver : {rarityDistribution.silver}</p>
          <p>Common : {rarityDistribution.common}</p> */}

          {accountStatus !== stripeAccountStatus.completed ? (
            <>
              <div>Before selling a booster you had to create a store</div>
              <Link className={`${globalStyles.unset}`} to="/store">
                <button
                  className={`${globalStyles.button}  ${styles.fullWidth}`}
                >
                  <h4>Create a shop</h4>
                </button>
              </Link>
            </>
          ) : (
            <>
              <h2>
                {booster.is_in_store ? <>Price : </> : <>Set Price : </>}
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={boosterPrice}
                  onChange={handleBoosterPrice}
                  className={styles.priceInput}
                  disabled={
                    booster.is_in_store || (cards && cards.length % 9 > 0)
                  }
                  max={20}
                />
                €
              </h2>
              <div>
                You will get {Math.max(0, boosterPrice * 0.9 - 0.5).toFixed(2)}€
                per sales
              </div>
              <button
                onClick={handleAddToStore}
                className={globalStyles.button}
                disabled={cards && (cards.length % 9 > 0 || cards.length === 0)}
              >
                {booster.is_in_store ? (
                  <h4> {t("boosters.addToStore.remove")}</h4>
                ) : (
                  <h4> {t("boosters.addToStore.add")}</h4>
                )}
              </button>
              <button onClick={onClose} className={globalStyles.button}>
                <h4> {t("cancel")}</h4>
              </button>
              <div>
                <FaCircleInfo /> {t("boosters.preview.infoNumberCards")}
              </div>
              <div>
                <FaCircleInfo /> {t("boosters.addToStore.removing")}
              </div>
              <div>
                <FaCircleInfo /> {t("boosters.addToStore.agreements")}
              </div>
              <div>
                <FaCircleInfo /> {t("boosters.addToStore.warning")}
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddBoosterToStoreModal;
